import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, MapPin, Calendar, Clock, Copy, Share2, FileText, ExternalLink, ChevronDown } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { track } from "../../lib/tracking";
import { googleCalendarUrl, outlookCalendarUrl, downloadICS } from "../../lib/calendar";
import { toast } from "sonner";

export default function ReservaConfirmada() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [trackedOnce, setTrackedOnce] = useState(false);

  useSEO({
    title: "Reserva confirmada",
    description: "Sua reserva foi confirmada com sucesso. Veja os detalhes.",
  });

  useEffect(() => {
    if (!token) { setError("Link inválido"); return; }
    api.get(`/public/reservations/${id}`, { params: { token } })
      .then(({ data }) => {
        setInfo(data);
        if (!trackedOnce) {
          track("reservation_confirmed", { reservation_id: id, total: data.reservation.amount });
          setTrackedOnce(true);
        }
      })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Reserva não encontrada"));
  }, [id, token, trackedOnce]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">😕</div>
          <h1 className="font-display text-2xl text-[#0A1128] mb-2">Não foi possível abrir sua reserva</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="text-[#D4AF37] font-semibold underline">Voltar ao início</Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Carregando reserva...</div>;
  }

  const r = info.reservation;
  const sp = info.space;
  const venue = info.venue;
  const startD = new Date(r.start_time);
  const endD = new Date(r.end_time);

  const dateStr = startD.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const timeStr = `${startD.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} às ${endD.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;

  const photo = sp.photos?.[0]
    ? (sp.photos[0].startsWith("http") ? sp.photos[0] : `${process.env.REACT_APP_BACKEND_URL}${sp.photos[0]}`)
    : null;

  const calendarPayload = {
    title: `Reserva ${sp.name} · VM Corporate`,
    startISO: r.start_time,
    endISO: r.end_time,
    description: `Reserva confirmada na VM Corporate.\nReserva: ${id}\n${venue.address}`,
    location: venue.address,
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(venue.address).then(() => toast.success("Endereço copiado!"));
  };

  const shareWhatsapp = () => {
    const txt = `🎉 Reserva confirmada na VM Corporate!\n\n📍 ${sp.name}\n🗓 ${dateStr}\n⏰ ${timeStr}\n\nEndereço: ${venue.address}\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const mapsQuery = encodeURIComponent(venue.address);
  const mapsEmbedSrc = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;

  return (
    <div className="min-h-screen bg-gray-50 pb-12" data-testid="reserva-confirmada-page">
      {/* Header verde */}
      <div className="bg-emerald-600 text-white py-10 px-6 text-center">
        <CheckCircle2 size={56} className="mx-auto mb-3" />
        <h1 className="font-display text-3xl sm:text-4xl font-light mb-1">Reserva confirmada!</h1>
        <p className="text-emerald-50 text-sm">Seu acesso está garantido. Veja os detalhes:</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5 -mt-6">
        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="reservation-card">
          {photo && <img src={photo} alt={sp.name} className="w-full aspect-[16/9] object-cover" />}
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{sp.category}</div>
              <div className="text-xl font-bold text-[#0A1128]">{sp.name}</div>
            </div>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center gap-3 text-gray-700">
                <Calendar size={18} className="text-[#0A1128] shrink-0" />
                <span className="capitalize">{dateStr}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Clock size={18} className="text-[#0A1128] shrink-0" />
                {timeStr}
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <MapPin size={18} className="text-[#0A1128] shrink-0" />
                {venue.address}
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded">
                {r.payment_status === "paid" ? "Pago" : "Aguardando"}
              </div>
              <div className="text-2xl font-black text-[#0A1128]">{formatBRL(r.amount)}</div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden" data-testid="map-block">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="font-bold text-[#0A1128]">Como chegar</div>
            <a
              href={venue.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-maps"
              className="text-xs font-semibold text-[#D4AF37] inline-flex items-center gap-1"
            >
              Abrir no Maps <ExternalLink size={12} />
            </a>
          </div>
          <iframe
            title="Mapa do VM Corporate"
            src={mapsEmbedSrc}
            className="w-full aspect-[16/9] block"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="p-3 flex items-center justify-between text-xs">
            <span className="text-gray-600 truncate">{venue.address}</span>
            <button
              type="button"
              onClick={copyAddress}
              data-testid="copy-address"
              className="text-[#D4AF37] font-semibold inline-flex items-center gap-1 shrink-0 ml-2"
            >
              <Copy size={12} /> Copiar
            </button>
          </div>
        </div>

        {/* Instructions */}
        {info.access_instructions && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5" data-testid="instructions-block">
            <div className="font-bold text-[#0A1128] mb-2">Instruções de acesso</div>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{info.access_instructions}</p>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setCalOpen((v) => !v)}
              data-testid="add-to-calendar"
              className="w-full bg-[#0A1128] text-white rounded-xl py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D] active:scale-[0.99] inline-flex items-center justify-center gap-2"
            >
              <Calendar size={16} /> Adicionar ao calendário <ChevronDown size={14} />
            </button>
            {calOpen && (
              <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" data-testid="calendar-menu">
                <a
                  href={googleCalendarUrl(calendarPayload)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 text-sm hover:bg-gray-50 text-[#0A1128]"
                  data-testid="cal-google"
                >Google Calendar</a>
                <a
                  href={outlookCalendarUrl(calendarPayload)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 text-sm hover:bg-gray-50 text-[#0A1128]"
                  data-testid="cal-outlook"
                >Outlook</a>
                <button
                  type="button"
                  onClick={() => { downloadICS(calendarPayload); setCalOpen(false); }}
                  data-testid="cal-ics"
                  className="block w-full text-left px-4 py-3 text-sm hover:bg-gray-50 text-[#0A1128]"
                >Apple / .ICS</button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={shareWhatsapp}
            data-testid="share-whatsapp"
            className="bg-emerald-500 text-white rounded-xl py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-emerald-600 active:scale-[0.99] inline-flex items-center justify-center gap-2"
          >
            <Share2 size={16} /> Compartilhar
          </button>
        </div>

        <div className="text-center pt-2">
          <Link to="/portal/reservas" className="text-[#D4AF37] font-semibold underline inline-flex items-center gap-1" data-testid="view-my-reservations">
            <FileText size={14} /> Ver minhas reservas
          </Link>
        </div>

        <div className="text-center text-xs text-gray-500 pt-4">
          Enviamos os detalhes para {r.guest_email}<br />
          {r.guest_phone && <>E também via WhatsApp para {r.guest_phone}</>}
        </div>
      </div>
    </div>
  );
}
